import { ITU_CODE } from "@nav-games/morse";

/// The Morse "dichotomic" tree. Start at the top and read down to a
/// letter: a DOT takes the left branch, a DASH the right. It's built
/// straight from ITU_CODE so it can never drift from the table — each
/// letter's code is the path of branches from the root to its node.

interface TNode {
  code: string;
  letter: string | null;
  dot: TNode | null;
  dash: TNode | null;
  x: number;
  depth: number;
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const X_GAP = 46;
const Y_GAP = 78;
const MARGIN = 28;
const R = 16;

function buildTree(): TNode {
  const root: TNode = { code: "", letter: null, dot: null, dash: null, x: 0, depth: 0 };
  for (const c of LETTERS) {
    const code = ITU_CODE[c];
    if (!code) continue;
    let node = root;
    for (const sym of code) {
      if (sym === ".") {
        node.dot ??= { code: node.code + sym, letter: null, dot: null, dash: null, x: 0, depth: 0 };
        node = node.dot;
      } else {
        node.dash ??= { code: node.code + sym, letter: null, dot: null, dash: null, x: 0, depth: 0 };
        node = node.dash;
      }
    }
    node.letter = c;
  }
  return root;
}

/// In-order layout: leaves take successive x slots, parents centre over
/// their children. Gives the classic fan shape regardless of which
/// branches exist.
function layout(root: TNode): { maxX: number; maxDepth: number } {
  let leaf = 0;
  let maxDepth = 0;
  const walk = (n: TNode, depth: number) => {
    n.depth = depth;
    maxDepth = Math.max(maxDepth, depth);
    const kids = [n.dot, n.dash].filter((k): k is TNode => k !== null);
    if (kids.length === 0) {
      n.x = leaf++;
      return;
    }
    for (const k of kids) walk(k, depth + 1);
    n.x = kids.reduce((s, k) => s + k.x, 0) / kids.length;
  };
  walk(root, 0);
  return { maxX: leaf - 1, maxDepth };
}

export function MorseTree() {
  const root = buildTree();
  const { maxX, maxDepth } = layout(root);

  const px = (x: number) => MARGIN + R + x * X_GAP;
  const py = (d: number) => MARGIN + R + d * Y_GAP;
  const width = px(maxX) + R + MARGIN;
  const height = py(maxDepth) + R + MARGIN;

  const edges: React.ReactNode[] = [];
  const nodes: React.ReactNode[] = [];

  const visit = (n: TNode) => {
    const x = px(n.x);
    const y = py(n.depth);

    for (const [child, sym] of [
      [n.dot, "•"],
      [n.dash, "—"],
    ] as const) {
      if (!child) continue;
      const cx = px(child.x);
      const cy = py(child.depth);
      edges.push(
        <line
          key={`e-${n.code}-${sym}`}
          x1={x}
          y1={y}
          x2={cx}
          y2={cy}
          stroke="#b9a77a"
          strokeWidth={1.5}
        />,
      );
      // Symbol label at the upper end of the edge, just clear of the
      // parent node, so dot vs dash is obvious at a glance.
      const lx = x + (cx - x) * 0.34;
      const ly = y + (cy - y) * 0.34;
      edges.push(
        <g key={`l-${n.code}-${sym}`}>
          <circle cx={lx} cy={ly} r={8} fill="#faf6e8" stroke="#d4cbb0" />
          <text
            x={lx}
            y={ly}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={sym === "•" ? 13 : 11}
            fill={sym === "•" ? "#2f6f4f" : "#b8302a"}
          >
            {sym}
          </text>
        </g>,
      );
      visit(child);
    }

    if (n.letter) {
      nodes.push(
        <g key={`n-${n.code}`}>
          <circle cx={x} cy={y} r={R} fill="#fff" stroke="#b8302a" strokeWidth={1.5} />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={15}
            fontWeight={700}
            fill="#222"
          >
            {n.letter}
          </text>
        </g>,
      );
    } else {
      // The empty root — the starting point of every letter.
      nodes.push(
        <g key="n-root">
          <circle cx={x} cy={y} r={R} fill="#faf6e8" stroke="#b9a77a" strokeWidth={1.5} />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9}
            fill="#7a6f50"
          >
            start
          </text>
        </g>,
      );
    }
  };

  visit(root);

  return (
    <div className="card">
      <h3>Tree view</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Start at the top and read down to a letter. A <strong style={{ color: "#2f6f4f" }}>•&nbsp;dot</strong>{" "}
        follows the left branch, a <strong style={{ color: "#b8302a" }}>—&nbsp;dash</strong> the
        right. e.g. dash→dot spells <strong>N</strong> (—•). Scroll
        sideways to see the whole tree.
      </p>
      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Morse code dichotomic tree"
          style={{ display: "block" }}
        >
          {edges}
          {nodes}
        </svg>
      </div>
    </div>
  );
}
